const MP4_MIME_TYPE = 'audio/mp4';
const MP4A_CODECS_MIME_TYPE = `${MP4_MIME_TYPE}; codecs=mp4a`;

/**
 * window.MediaRecorder with an SSR guard. Used as the native recording path on browsers that
 * support audio/mp4 (e.g. Safari). Non-Safari browsers fall back to opus-media-recorder.
 */
export const BrowserMediaRecorder = typeof window !== 'undefined' ? window.MediaRecorder : null;

/**
 * MIME type for opus-media-recorder fallback (WAV).
 */
export const OPUS_WAV_MIME_TYPE = 'audio/wav';

/**
 * Safari supports audio/mp4 (AAC) natively; use it so recordings play everywhere.
 * Returns null if neither mp4 MIME type is supported, falling back to opus-media-recorder.
 * @returns {string|null} MIME type for native recorder (e.g. 'audio/mp4') or null to use opus fallback.
 */
export const getNativeRecorderMimeType = () => {
  if (!BrowserMediaRecorder || !BrowserMediaRecorder.isTypeSupported) return null;
  if (BrowserMediaRecorder.isTypeSupported(MP4A_CODECS_MIME_TYPE)) return MP4A_CODECS_MIME_TYPE;
  if (BrowserMediaRecorder.isTypeSupported(MP4_MIME_TYPE)) return MP4_MIME_TYPE;
  return null;
};

/**
 * Base MIME type from native string (strip codecs), e.g. 'audio/mp4; codecs=mp4a' -> 'audio/mp4'.
 * @param {string} nativeMimeType
 * @returns {string}
 */
const baseMimeTypeFromNative = (nativeMimeType) => (nativeMimeType || '').split(';')[0].trim();

/**
 * Opus-media-recorder worker options (WASM paths). Caller provides encoderWorkerFactory.
 * @param {function} encoderWorkerFactory
 * @returns {{ encoderWorkerFactory: function, OggOpusEncoderWasmPath: string, WebMOpusEncoderWasmPath: string }}
 */
export const getOpusWorkerOptions = (encoderWorkerFactory) => {
  const OggOpusEncoderWasmPath = `${process.env.PUBLIC_URL}/opus-media-recorder/OggOpusEncoder.wasm`;
  const WebMOpusEncoderWasmPath = `${process.env.PUBLIC_URL}/opus-media-recorder/WebMOpusEncoder.wasm`;

  return { encoderWorkerFactory, OggOpusEncoderWasmPath, WebMOpusEncoderWasmPath };
};

/**
 * Creates and starts a native MediaRecorder with correct chunked blob assembly.
 * Extracted to avoid duplication between WebMediaRecorder and useMediaRecorder.
 * @param {MediaStream} stream - Audio stream to record.
 * @param {string} mimeType - MIME type for the recorder (e.g. 'audio/mp4').
 * @param {typeof MediaRecorder} NativeRecorderClass - Native MediaRecorder constructor.
 * @param {{ onStop: function(Blob): void, onError: function(Event): void }} callbacks
 * @returns {MediaRecorder} The started recorder instance.
 */
export const createNativeRecorderSetup = (stream, mimeType, NativeRecorderClass, { onStop, onError }) => {
  const chunks = [];
  const recorder = new NativeRecorderClass(stream, { mimeType });

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  recorder.onstop = () => {
    if (chunks.length === 0) return;
    const finalBlob = new Blob(chunks, { type: baseMimeTypeFromNative(mimeType) });
    if (finalBlob.size > 0) onStop(finalBlob);
  };
  recorder.onerror = onError;

  recorder.start();
  return recorder;
};
